import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Wrench, 
  Loader2, 
  CheckCircle2, 
  AlertTriangle,
  Users,
  Search,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const RPATProgressRepairCard = ({ api }) => {
  const [repairing, setRepairing] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [result, setResult] = useState(null);
  const [diagResult, setDiagResult] = useState(null);

  const handleDiagnose = async () => {
    setDiagnosing(true);
    setDiagResult(null);
    
    try {
      const res = await api.get('/rpat/certification/admin/diagnose-phase6-issues');
      setDiagResult(res.data);
      
      if (res.data.users_with_issues > 0) {
        toast.warning(`Found ${res.data.users_with_issues} users with issues`, {
          duration: 5000
        });
      } else {
        toast.success('No issues found!');
      }
    } catch (err) {
      console.error('Diagnose failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to diagnose');
    } finally {
      setDiagnosing(false);
    }
  };

  const handleBulkRepair = async () => {
    if (!window.confirm(
      'This will scan ALL users and repair any broken Phase 6/7 states.\n\n' +
      'Users with:\n' +
      '• Phase 6 "completed" but no labs loaded\n' +
      '• Phase 7 locked but Phase 6 actually complete\n\n' +
      'will be automatically fixed.\n\nContinue?'
    )) {
      return;
    }

    setRepairing(true);
    setResult(null);

    try {
      const res = await api.post('/rpat/certification/admin/bulk-repair-all-users');
      
      setResult(res.data);
      
      if (res.data.total_repaired > 0) {
        toast.success(`Repaired ${res.data.total_repaired} users!`, {
          description: 'Their Phase 6/7 progress has been restored',
          duration: 5000
        });
      } else {
        toast.info(`Scanned ${res.data.total_scanned} users - no repairs needed`);
      }
    } catch (err) {
      console.error('Bulk repair failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to run bulk repair');
    } finally {
      setRepairing(false);
    }
  };

  return (
    <Card className="border-2 border-orange-500/20">
      <CardHeader className="bg-gradient-to-r from-orange-500/5 to-amber-500/5">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-orange-500/20">
            <Wrench className="h-5 w-5 text-orange-500" />
          </div>
          <div>
            <CardTitle className="flex items-center gap-2">
              R-PAT Progress Repair
              <Badge variant="outline" className="text-xs">Admin Tool</Badge>
            </CardTitle>
            <CardDescription>
              Fix broken Phase 6/7 states for ALL users with one click
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 pt-6">
        <div className="p-4 rounded-lg bg-muted/50 space-y-2">
          <p className="text-sm font-medium">This tool automatically repairs:</p>
          <ul className="text-sm text-muted-foreground space-y-1 ml-4">
            <li>• Users with Phase 6 &quot;completed&quot; but no labs loaded</li>
            <li>• Users with Phase 7 locked despite Phase 6 being complete</li>
            <li>• Syncs external sourcing lab completions</li>
          </ul>
        </div>

        {/* Previous Result */}
        {result && (
          <div className={`p-4 rounded-lg border ${
            result.total_repaired > 0 
              ? 'bg-green-50 border-green-200 dark:bg-green-950/20' 
              : 'bg-blue-50 border-blue-200 dark:bg-blue-950/20'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              {result.total_repaired > 0 ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-blue-600" />
              )}
              <span className="font-medium">
                {result.total_repaired > 0 
                  ? `Repaired ${result.total_repaired} users`
                  : 'No users needed repair'
                }
              </span>
            </div>
            
            <p className="text-sm text-muted-foreground mb-2">
              Scanned {result.total_scanned} total user records
            </p>

            {result.repaired_users?.length > 0 && (
              <div className="mt-3 max-h-40 overflow-y-auto">
                <p className="text-xs font-medium mb-1">Repaired Users:</p>
                {result.repaired_users.map((user, idx) => (
                  <div key={idx} className="text-xs py-1 border-b border-green-200 last:border-0">
                    <span className="font-medium">{user.name}</span>
                    <span className="text-muted-foreground ml-2">({user.email})</span>
                    <div className="text-muted-foreground">{user.repairs?.join(', ')}</div>
                  </div>
                ))}
              </div>
            )}

            {result.errors?.length > 0 && (
              <div className="mt-2 text-xs text-red-600">
                {result.errors.length} error(s) occurred
              </div>
            )}
          </div>
        )}

        {/* Diagnose Result */}
        {diagResult && (
          <div className={`p-4 rounded-lg border ${
            diagResult.users_with_issues > 0 
              ? 'bg-red-50 border-red-200 dark:bg-red-950/20' 
              : 'bg-green-50 border-green-200 dark:bg-green-950/20'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              {diagResult.users_with_issues > 0 ? (
                <AlertTriangle className="h-5 w-5 text-red-600" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              )}
              <span className="font-medium">
                {diagResult.users_with_issues > 0 
                  ? `Found ${diagResult.users_with_issues} users with issues`
                  : 'No issues found'
                }
              </span>
            </div>
            
            <p className="text-sm text-muted-foreground mb-2">
              Scanned {diagResult.total_users_scanned} user records
            </p>

            {diagResult.issues?.length > 0 && (
              <div className="mt-3 max-h-60 overflow-y-auto">
                <p className="text-xs font-medium mb-1">Users with Issues:</p>
                {diagResult.issues.map((user, idx) => (
                  <div key={idx} className="text-xs py-2 border-b border-red-200 last:border-0">
                    <div className="font-medium">{user.name} ({user.email})</div>
                    <div className="text-muted-foreground">
                      Phase 6: {user.phase_6_status} | Labs: {user.phase_6_labs_length} ({user.phase_6_labs_type}) | Phase 7: {user.phase_7_status}
                    </div>
                    <div className="text-red-600">{user.issues?.join(', ')}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            onClick={handleDiagnose}
            disabled={diagnosing || repairing}
            variant="outline"
            className="flex-1"
          >
            {diagnosing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Diagnosing...
              </>
            ) : (
              <>
                <Search className="mr-2 h-4 w-4" />
                Diagnose Issues
              </>
            )}
          </Button>
          
          <Button
            onClick={handleBulkRepair}
            disabled={repairing || diagnosing}
            className="flex-1 bg-orange-500 hover:bg-orange-600"
          >
            {repairing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Repairing...
              </>
            ) : (
              <>
                <Users className="mr-2 h-4 w-4" />
                Repair All Users
              </>
            )}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground text-center">
          Run this after deploying fixes to repair all affected production users
        </p>
      </CardContent>
    </Card>
  );
};

export default RPATProgressRepairCard;
